// GestureRecognizer.js
export default class GestureRecognizer {
  constructor(element, onGesture) {
    this.element = element;
    this.onGesture = onGesture || null;
    this.points = [];
    this.isTracking = false;

    // Tweak these thresholds for your game's feel
    this.MIN_UP_DISTANCE = 100;     // Minimum pixels moved up
    this.MIN_CURVE_DEVIATION = 40;  // How "fat" the curve needs to be
    this.MIN_POWER = 800;           // Speed (px/s) for a fast swipe
    this.MIN_X_TOLERANCE = 20;      // Deadzone to ignore tiny horizontal wiggles

    this.bindEvents();
  }

  bindEvents() {
    this._onDown = (e) => {
      this.isTracking = true;
      this.points = [{ x: e.clientX, y: e.clientY, time: e.timeStamp }];
    };
    this._onMove = (e) => {
      if (!this.isTracking) return;
      this.points.push({ x: e.clientX, y: e.clientY, time: e.timeStamp });
    };
    this._onUp = (e) => {
      if (!this.isTracking) return;
      this.isTracking = false;
      this.analyzeGesture();
    };
    this._onCancel = () => {
      this.isTracking = false;
    };

    this.element.addEventListener('pointerdown', this._onDown);
    this.element.addEventListener('pointermove', this._onMove);
    this.element.addEventListener('pointerup', this._onUp);
    this.element.addEventListener('pointercancel', this._onCancel);
  }

  destroy() {
    this.element.removeEventListener('pointerdown', this._onDown);
    this.element.removeEventListener('pointermove', this._onMove);
    this.element.removeEventListener('pointerup', this._onUp);
    this.element.removeEventListener('pointercancel', this._onCancel);
  }

  getPointToLineDistance(point, lineStart, lineEnd) {
    const numerator = Math.abs(
      (lineEnd.x - lineStart.x) * (lineStart.y - point.y) - 
      (lineStart.x - point.x) * (lineEnd.y - lineStart.y)
    );
    const denominator = Math.hypot(lineEnd.x - lineStart.x, lineEnd.y - lineStart.y);
    return denominator === 0 ? 0 : numerator / denominator;
  }

  analyzeGesture() {
    if (this.points.length < 3) return; 

    const startPoint = this.points[0];
    const endPoint = this.points[this.points.length - 1];

    // 1. CALCULATE POWER
    const durationMs = endPoint.time - startPoint.time;
    if (durationMs === 0) return;
    const totalDistance = Math.hypot(endPoint.x - startPoint.x, endPoint.y - startPoint.y);
    const power = (totalDistance / durationMs) * 1000; 

    // 2. CHECK UPWARD DIRECTION
    const yDistance = startPoint.y - endPoint.y; 
    if (yDistance < this.MIN_UP_DISTANCE) {
      return; // Ignore down swipes or tiny taps
    }

    // 3. CHECK LEFT OR RIGHT DIRECTION
    const xDistance = endPoint.x - startPoint.x;
    let horizontalDirection = "Center";
    
    if (xDistance > this.MIN_X_TOLERANCE) {
      horizontalDirection = "Right";
    } else if (xDistance < -this.MIN_X_TOLERANCE) {
      horizontalDirection = "Left";
    }

    // 4. CHECK CURVE DEVIATION
    let maxDeviation = 0;
    for (let i = 1; i < this.points.length - 1; i++) {
      const distance = this.getPointToLineDistance(this.points[i], startPoint, endPoint);
      if (distance > maxDeviation) {
        maxDeviation = distance;
      }
    }

    // 5. FINAL RESULT
    const result = {
      type: maxDeviation >= this.MIN_CURVE_DEVIATION ? 'curved' : 'straight',
      direction: horizontalDirection,
      power: Math.round(power),
      curve: Math.round(maxDeviation),
      startPoint: { x: startPoint.x, y: startPoint.y },
      endPoint: { x: endPoint.x, y: endPoint.y },
      durationMs: Math.round(durationMs),
    };

    if (this.onGesture) {
      this.onGesture(result);
    }
  }
}